import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import usePageMeta from '../utils/usePageMeta';
import { track, EVENTS } from '../services/analytics';
import { fetchTestimonials, fetchLandingStats } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { postSignInDestination } from '../utils/postSignIn';
import useGoogleSignIn from '../hooks/useGoogleSignIn';
import useLandingInstrumentation from '../hooks/useLandingInstrumentation';
import SigningInOverlay from '../components/SigningInOverlay';
import MarketingHeader from '../components/MarketingHeader';
import TryHero, { TRY_HERO_ENABLED } from '../components/landing/TryHero';
import AstraLite from '../components/landing/astraLite/AstraLite';
import { AstraChatHero } from '../components/landing/astraChat/AstraChat';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { previewToken } from '../services/astraLite';
import { ASTRA_LITE } from '../content/astraLite';
import FooterV2 from '../components/landing/v2/FooterV2';
import { BuildStrip, BuildCards } from '../components/landing/home/BuildTools';
import PlanningSection from '../components/landing/home/PlanningSection';
import WithCounselor from '../components/landing/home/WithCounselor';
import Gallery from '../components/landing/home/Gallery';
import HeroTestimonials from '../components/landing/home/Testimonials';
import { StartFree, SECONDARY_BUTTON } from '../components/landing/home/parts';
import { ProofStrip, MilestoneRibbon } from '../components/landing/home/ProofStrip';
import {
    PAGE_TITLE, PAGE_DESCRIPTION, HERO, BUILD_SECTION, TOOLS, PRIVACY_LINE, COUNSELOR_LINE,
    CONTRAST, MORE_TOOLS, HOW, TRENDS, ORDER, FREE_READ,
} from '../content/landingHome';
import { Contrast, TrendsBand, HowItWorks } from '../components/landing/home/BuilderBlocks';
import { HomeCollegeLinks } from '../components/landing/home/CollegeLinks';
import { DEMO_FILM, FILM_HASH } from '../data/demoFilm';
import { useSchoolEntry } from '../utils/schoolEntry';
import MastersWaitlist, { mastersSectionVisible } from '../components/landing/home/MastersWaitlist';
import { resolveVisitorRegion } from '../utils/visitorRegion';
import { mastersFlagOn } from '../hooks/useMastersFlag';
import MastersLink, { mastersLinkVisible } from '../components/landing/home/MastersLink';
import {
    MASTERS_BEFORE, STORY_PAGE_TITLE, STORY_PAGE_DESCRIPTION, STORY_HERO, STORY_ORDER,
} from '../content/landingHome';
import useLandingStory from '../hooks/useLandingStory';
import useSeasonPassPrice, { ONE_PAYMENT_FLAG } from '../hooks/useSeasonPassPrice';
import {
    QuotesBand, StorySteps, PlanGrid, BuildBand, CounselorCard, CostBand, TourLine,
} from '../components/landing/home/StoryBands';

// ============================================================================
// THE LANDING PAGE (`/`). LandingRoute renders it eagerly from the first
// paint, with no flag read and no fallback (#2526: the old gate showed the
// retired LandingPage.jsx first on every cold load).
//
// Founder pick A, "Set yourself apart" (2026-09-29, mock round
// https://claude.ai/artifact/XUuXdLSfBgc582sMGgLHsk), amended by the founder
// the same day; ADR 0022 records the pick, the amendments and why B, C and D
// lost. The page went from 53.5 phone screens to about ten, and the Build
// tools moved from screen 26 to the first screen and a half.
//
// The order, top to bottom, and why each thing is where it is:
//   hero          the headline, one sub-line, Start free, and the quotes
//                 carousel (every real quote) on the first phone screen
//                 ("testimonials are important. make sure everybody sees
//                 it", then "we can do the carousel in the top itself").
//   build strip   the four tools, each jumping to its card.
//   free read     TryHero at #see-your-odds: the Google Ads landing anchor
//                 and the funnel. Same component, same id, same events.
//   evidence      what the most selective colleges say they look for,
//                 hero-grade (the founder's favourite part), ending on the
//                 bridge into the tools with a Start free.
//   build         the four cards in full.
//   planning      "And we plan the rest", compact.
//   with counselor Stratia alongside a counselor (#4972): the groundwork
//                 it does, sharing, Start free.
//   gallery       six real product screens, each with its guide, and the
//                 link to /tour, which carries every accurate screen
//                 (decision A, the same day: the screens come back below
//                 the fold, most important things first).
//   privacy line, counselor line, footer.
//
// Gone, and where it went (ADR 0022): the film band (every /#film link now
// opens the film file itself, below), "Why trust Stratia?" (one privacy line
// here, the rest on /student-data-principles), the mission band, the flagship
// Launchpad card, The Read scene, the stats band, Astra's section, the
// fourteen feature rows, the parent band, the comparison table and the
// schools band (one counselor line, linking /for-schools). The product
// screens came back the same day (decision A): six in the gallery below
// planning, every accurate one on /tour.
//
// The story order (#5128, founder pick A of the redesign mocks #5093, behind
// landing_story; hooks/useLandingStory.js has the flag and the preview).
// Ask Astra stays first with #see-your-odds, the proof strip and (India) the
// Master's abroad section stay where they are; Astra's heading becomes the
// promise. Then: quotes, the three steps, Look up a college, everything the
// plan covers, the builder tools as one band, the counselor card, what it
// costs, one line to /tour. Gone in it: the builder hero and its tool strip,
// the trends band (one sourced line moved into the Build band), the four
// tools in full, And we plan the rest, the full counselor section and the
// gallery. Off: the order below, unchanged.
//
// Analytics (the Desk's landing watch, #2512): every section carries
// data-landing-section and every link or button data-cta, ids from
// content/landingIds.js, named for what they mean, never for their text.
// Every Start free passes its own CTA id as the sign-in entry.
// ============================================================================

// Both deferred anchor scrolls run in timers, so a throw here is an
// unhandled error nobody catches. The element may be gone by the time the
// timer fires, and scrollIntoView is absent where layout is not implemented
// (jsdom: the throw that failed vitest gates at random, #2800).
function scrollToAnchor(el, reduced) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}

export default function LandingPageV2() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { signIn: handleSignIn, phase: signInPhase } = useGoogleSignIn();
    const tools = TOOLS;
    // null while loading; [] when the endpoint failed or has nothing (both
    // quote blocks then render nothing, never a placeholder).
    const [testimonials, setTestimonials] = useState(null);

    // Sections that mount after the first render (the free read once auth
    // resolves) re-attach the section observer.
    const rootRef = useRef(null);
    // Astra Lite (#2549, ADR 0023) runs the free read and the open chat only
    // when astra_lite, the signed-out Astra surface's kill switch, is on for
    // everyone (a public flag) or this tab carries the founder's preview
    // token; the server re-checks both on every call. Otherwise the shipped
    // TryHero renders, unchanged.
    // A signed-in account sees the free read only to dogfood Astra Lite: with
    // the preview token, or when astra_lite resolves on for THIS account (the
    // allowlist; the server re-checks the account's verified token). Every
    // other signed-in user keeps today's page, with no free-read block
    // (founder, 2026-09-30: "the whole hero section is gone").
    // The page's one flag read: astra_lite, public for a guest, plus the
    // account's own read for a signed-in allowlisted account.
    const { publicFlags, flags, forEmail } = useFeatureFlags({ mine: Boolean(currentUser), public: true }) || {};
    // The story order (#5128): the public flag (seeded into the prerendered
    // page, so its first paint carries it) or a team device's preview.
    const story = useLandingStory(publicFlags);
    // The title and Google description change with the headline (question 5).
    usePageMeta(story
        ? { title: STORY_PAGE_TITLE, description: STORY_PAGE_DESCRIPTION, path: '/' }
        : { title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: '/' });
    // The cost band's prices: the visitor's Season Pass (the rupee pass for
    // an India visitor while india_masters is on, read after mount, so the
    // prerendered markup is the dollar line), and Through Graduation once
    // one_payment_passes is on for everyone (question 3). 'landing' is not a
    // counted price surface (PRICE_SURFACES): the band's reach is counted as
    // its landing_section_seen.
    const { price: passPrice, region: priceRegion } = useSeasonPassPrice({ surface: 'landing' });
    const graduationPrice = publicFlags?.[ONE_PAYMENT_FLAG] ? priceRegion.throughGraduation() : null;
    const [astraSpent, setAstraSpent] = useState(false);
    const [preview] = useState(() => Boolean(previewToken()));
    // (If the route refuses or its budget is spent, AstraLite calls
    // onUnavailable and the shipped free read takes the box, as for a guest,
    // rather than the section vanishing again.)
    const internalAstra = Boolean(currentUser) && (preview
        || (Boolean(flags?.astra_lite) && forEmail === currentUser.email));
    const astraLite = !astraSpent && (Boolean(publicFlags?.astra_lite) || preview || internalAstra);
    // Astra's open chat (#2725, founder pick B of the "Ask Anything" mocks):
    // under the same switch (or the preview, or an allowlisted account), Astra
    // leads the page and carries #see-your-odds, the Google Ads landing
    // anchor; the free-read block below is not rendered (the chat's "Make it
    // about me" is the picture now) and the planning section's Astra box goes
    // (merged into the hero). A closed route or a spent budget brings back
    // today's page, free read included, so the ad landing never goes blank.
    // A college page's "See your odds" link (#2608) keeps today's flow: it
    // came for that school's read.
    const [chatSpent, setChatSpent] = useState(false);
    // The Master's abroad section (#4967, ADR 0059): India visitors only (the
    // one region signal, utils/visitorRegion.js, ?region=in for QA), and only
    // while india_masters is on for this visitor: on for everyone (a public
    // flag), or on for this signed-in account (Just us). Off, the signal
    // changes nothing. The region is read after mount: the page is
    // prerendered, and its first render must match the markup.
    const [visitor, setVisitor] = useState(null);
    useEffect(() => { setVisitor(resolveVisitorRegion()); }, []);
    const indiaMastersOn = mastersFlagOn({ publicFlags, flags, forEmail, currentUser });
    const mastersShown = Boolean(visitor) && mastersSectionVisible({ flagOn: indiaMastersOn, region: visitor.region });
    // The default region's one line to /masters (#4985), low on the page;
    // India has the full section instead. Asked of the region strategy.
    const mastersLinkShown = Boolean(visitor) && mastersLinkVisible({ flagOn: indiaMastersOn, region: visitor.region });
    useLandingInstrumentation(rootRef, [Boolean(currentUser), tools.length, mastersShown, mastersLinkShown, story]);
    // A college page's "See your odds" link (#2608) names its school; the free
    // read opens pre-set for it. Null for every other arrival.
    const schoolEntry = useSchoolEntry();
    const chatHero = TRY_HERO_ENABLED && !chatSpent && !schoolEntry && (!currentUser || internalAstra)
        && (Boolean(publicFlags?.astra_lite) || preview || internalAstra);

    useEffect(() => {
        let alive = true;
        fetchTestimonials().then((list) => { if (alive) setTestimonials(Array.isArray(list) ? list : []); });
        return () => { alive = false; };
    }, []);

    // The proof strip and milestone ribbon (#3820): null until read, and
    // nothing renders unless the server says live (the founder's Go live).
    const [landingStats, setLandingStats] = useState(null);
    useEffect(() => {
        let alive = true;
        fetchLandingStats().then((s) => { if (alive) setLandingStats(s); });
        return () => { alive = false; };
    }, []);

    // The deferred hash scroll: a bare #hash resolves before React has
    // rendered the section, and #see-your-odds is an AD LANDING TARGET.
    //
    // #film is the one exception. The film band is gone, but emails, the
    // lifecycle templates, an ads sitelink and the /videos redirect all link
    // /#film, and every one of those readers clicked to watch the
    // walk-through. They get the film itself, not a page without it.
    useEffect(() => {
        const id = window.location.hash.slice(1);
        if (!id) return undefined;
        if (id === FILM_HASH) {
            window.location.replace(DEMO_FILM.src);
            return undefined;
        }
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        let tries = 0;
        const timer = setInterval(() => {
            const el = document.getElementById(id);
            if (el) {
                // Stop first: a throw after this point must not leave the
                // interval re-firing every 100ms.
                clearInterval(timer);
                scrollToAnchor(el, reduced);
            } else if (++tries > 20) {
                clearInterval(timer);
            }
        }, 100);
        return () => clearInterval(timer);
    }, []);

    // #see-your-odds can MOVE after the first paint (#2725): the live flags
    // turn the chat on after the build-time seed, or the route says the chat
    // is closed and the free read takes the anchor back. An ad visitor who
    // landed on the anchor follows it, in the first few seconds only and
    // never once they have scrolled themselves (review of #2725).
    const landedAt = useRef(Date.now());
    const userScrolled = useRef(false);
    useEffect(() => {
        const mark = () => { userScrolled.current = true; };
        window.addEventListener('wheel', mark, { passive: true });
        window.addEventListener('touchmove', mark, { passive: true });
        window.addEventListener('keydown', mark);
        return () => {
            window.removeEventListener('wheel', mark);
            window.removeEventListener('touchmove', mark);
            window.removeEventListener('keydown', mark);
        };
    }, []);
    const firstAnchor = useRef(true);
    useEffect(() => {
        if (firstAnchor.current) { firstAnchor.current = false; return undefined; }
        if (window.location.hash !== '#see-your-odds' || userScrolled.current
            || Date.now() - landedAt.current > 8000) return undefined;
        const t = setTimeout(() => {
            const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            scrollToAnchor(document.getElementById('see-your-odds'), reduced);
        }, 50);
        return () => clearTimeout(t);
    }, [chatHero]);

    // `entry`: the CTA that started sign-in (landingIds SIGNIN_ENTRIES).
    const handleGetStarted = (entry) => {
        if (currentUser) {
            postSignInDestination(currentUser.email).then(navigate);
        } else {
            handleSignIn(entry);
        }
    };

    // The blocks under the hero, by the key content/landingHome.js ORDER
    // names, so a new message reorders the page without touching it.
    const sections = {
        free_read: TRY_HERO_ENABLED && !chatHero && (!currentUser || internalAstra) && (
            <section id="see-your-odds" data-landing-section="free_read" className="scroll-mt-20 bg-[#FDFCF7] px-0 pb-8 sm:px-6 lg:px-8 lg:pb-16">
                {/* The free read speaks to the student. Its anchor and id are the
                    Google Ads landing target and never change; inside the box
                    every word is its own, and only its heading and line come
                    from the copy module (founder pick "1"). The line that used to
                    sit above it is gone (founder: "remove it and ship it"). */}
                {astraLite
                    ? <AstraLite onStart={handleSignIn} onUnavailable={() => setAstraSpent(true)} heading={FREE_READ.heading} sub={ASTRA_LITE.sub} school={schoolEntry} signedIn={Boolean(currentUser)} />
                    : <TryHero onSignIn={() => handleSignIn('free_read_keep')} heading={FREE_READ.heading} sub={FREE_READ.sub} school={schoolEntry} />}
            </section>
        ),
        how: <HowItWorks copy={HOW} />,
        colleges: <HomeCollegeLinks />,
        trends: <TrendsBand copy={TRENDS} onStart={handleGetStarted} />,
        build: <BuildCards tools={tools} onStart={handleGetStarted} more={MORE_TOOLS} />,
        planning: <PlanningSection onStart={handleGetStarted} hideAstra={chatHero} />,
        // Stratia alongside a counselor (#4972). Its master's line shows only
        // beside the Master's section, in the future tense.
        with_counselor: <WithCounselor onStart={handleGetStarted} masters={mastersShown} />,
        // Six real screens, then the full tour (founder decision A, 2026-09-29:
        // "They can still be there. just that the most important things only
        // appear first."). Loaded only as it nears the screen.
        gallery: <Gallery />,
    };

    // The story order's bands, by the key STORY_ORDER names. The free read
    // keeps its place first under the reach band whenever it shows (the
    // chat closed, a college page's "See your odds", a signed-in account).
    const storySections = {
        quotes: <QuotesBand testimonials={testimonials} onStart={handleGetStarted} />,
        story: <StorySteps onStart={handleGetStarted} />,
        colleges: <HomeCollegeLinks />,
        plan: <PlanGrid />,
        build: <BuildBand />,
        with_counselor: <CounselorCard masters={mastersShown} />,
        cost: <CostBand onStart={handleGetStarted} pass={passPrice} graduation={graduationPrice}
            oneFreeRead={Boolean(publicFlags?.one_free_read)} />,
        tour_line: <TourLine />,
    };

    return (
        <div ref={rootRef} className="min-h-screen bg-[#FDFCF7] lining-nums [font-feature-settings:'lnum'_1]" data-testid="landing-v2">
            <SigningInOverlay phase={signInPhase} />
            <MarketingHeader />
            <MilestoneRibbon stats={landingStats} />

            {chatHero && (
                <section id="see-your-odds" data-landing-section="hero_chat" data-testid="landing-hero-chat"
                    className="scroll-mt-20 bg-[#FDFCF7] px-0 pb-8 pt-20 sm:px-6 sm:pt-24 lg:px-8">
                    <AstraChatHero onStart={handleSignIn} onUnavailable={() => setChatSpent(true)}
                        heading={story ? STORY_HERO.heading : null} sub={story ? STORY_HERO.sub : null}
                        signedIn={Boolean(currentUser)} buildNames={tools.map((t) => t.name)} />
                </section>
            )}
            {/* Mock variant A (#3820): the proof strip directly under Ask Astra. */}
            {chatHero && <ProofStrip stats={landingStats} />}
            {MASTERS_BEFORE === 'hero' && mastersShown && (
                <MastersWaitlist region={visitor.region} regionSource={visitor.source}
                    signedInEmail={currentUser?.email || null} stats={landingStats} />
            )}
            {story ? (<>
                {/* Without the chat, the promise is the page's one h1, over
                    the free read (design.md section 4). */}
                {!chatHero && (
                    <div data-testid="landing-story-promise" className="mx-auto max-w-3xl px-4 pb-4 pt-24 text-center">
                        <h1 className="font-serif text-[30px] font-bold leading-[1.1] text-[#1A4D2E] [text-wrap:balance] sm:text-5xl">{STORY_HERO.heading}</h1>
                        <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-[#4A4A4A] [text-wrap:pretty] sm:text-lg">{STORY_HERO.sub}</p>
                    </div>
                )}
                {!chatHero && <ProofStrip stats={landingStats && { ...landingStats, reach: null }} />}
                {sections.free_read}
                {STORY_ORDER.map((k) => (
                    <React.Fragment key={k}>
                        {storySections[k]}
                    </React.Fragment>
                ))}
            </>) : (<>
            {/* ============ HERO ============ */}
            <section data-landing-section="hero" data-testid="landing-hero" className={`px-4 pb-8 sm:px-6 lg:px-8 lg:pb-14 ${chatHero ? 'pt-12 lg:pt-16' : 'pt-24 lg:pt-24'}`}>
                <div className="mx-auto max-w-4xl text-center">
                    {/* One h1 per page: with the chat leading, this headline is the second section's. */}
                    {React.createElement(chatHero ? 'h2' : 'h1', {
                        className: '[text-wrap:balance] mb-4 font-serif text-[36px] font-bold leading-[1.08] tracking-tight text-[#1A4D2E] sm:mb-5 sm:text-5xl lg:text-[56px]',
                    }, HERO.title)}
                    <p className="mx-auto mb-3 max-w-2xl text-base leading-relaxed text-[#4A4A4A] [text-wrap:balance] sm:text-xl">
                        {HERO.sub}
                    </p>
                    <Contrast text={CONTRAST} className="mb-5 sm:mb-6" />
                    <div className="mx-auto flex w-full max-w-xs flex-col items-stretch justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center">
                        <StartFree onStart={handleGetStarted} cta="hero_get_started" className="w-full sm:w-auto" />
                        {tools.length > 0 && (
                            <a
                                href="#build"
                                data-cta="hero_see_tools"
                                className={`${SECONDARY_BUTTON} w-full sm:w-auto`}
                            >
                                {BUILD_SECTION.heroLink(tools.length)}
                            </a>
                        )}
                    </div>
                    <HeroTestimonials testimonials={testimonials} />
                    <BuildStrip tools={tools} more={MORE_TOOLS} />
                </div>
            </section>
            {/* Without the chat, the strip sits under the hero instead, and
                without the visitor maps block (#4607): there it would push the
                free read, this layout's first action, past 2 phone screens
                (2.37 measured; landing-layout.spec.js). */}
            {!chatHero && <ProofStrip stats={landingStats && { ...landingStats, reach: null }} />}

            {ORDER.map((k) => (
                <React.Fragment key={k}>
                    {sections[k]}
                </React.Fragment>
            ))}
            </>)}

            {/* One privacy line, no heading, no cards (founder, 2026-09-29:
                the trust section was something visitors never reached). The
                claims are the ones /student-data-principles and the privacy
                policy make: the name, email and phone number are stripped
                before any model call (pii_scrubber / pii_scrub / guest_pii;
                narrowed to exactly those three by #2837, founder 2026-10-01)
                and student data is never sold. */}
            <div data-landing-section="privacy_line" className="bg-[#FDFCF7] px-4 pt-10 sm:px-6 lg:px-8">
                <p data-testid="landing-privacy-line" className="mx-auto max-w-3xl text-center text-base leading-relaxed text-[#4A4A4A]">
                    {PRIVACY_LINE.text}{' '}
                    <Link to={PRIVACY_LINE.to} data-cta="privacy_principles" className="font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4">
                        {PRIVACY_LINE.link}
                    </Link>.
                </p>
            </div>

            <div data-landing-section="counselor_line" className="bg-[#FDFCF7] px-4 pb-10 pt-4 sm:px-6 lg:px-8">
                <p data-testid="landing-counselor-line" className="mx-auto max-w-3xl text-center text-base text-[#4A4A4A]">
                    {COUNSELOR_LINE.lead}{' '}
                    <Link
                        to={COUNSELOR_LINE.to}
                        data-cta="counselor_for_schools"
                        onClick={() => track(EVENTS.COUNSELOR_DOOR_CLICKED, { source: 'landing_line' })}
                        className="font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4"
                    >
                        {COUNSELOR_LINE.link}
                    </Link>
                </p>
            </div>

            {mastersLinkShown && <MastersLink region={visitor.region} regionSource={visitor.source} />}

            <div data-landing-section="footer" data-cta="footer_link"><FooterV2 /></div>
        </div>
    );
}
